/**
 * User-facing auth error copy. Keeps enumeration-safe defaults for login
 * while giving actionable guidance on signup / OAuth collisions.
 */

export function friendlySignupError(error: Error | null): string {
  if (!error) return "Something went wrong. Please try again.";
  const msg = error.message;

  if (
    /already registered|already been registered|user already registered|email address is already registered/i.test(
      msg,
    )
  ) {
    return "An account with this email already exists. Sign in with your password, or use Sign in with Google with the same email.";
  }

  if (/invalid email|unable to validate email/i.test(msg)) {
    return "That email address doesn't look valid. Check for typos and try again.";
  }

  if (/password/i.test(msg) && /weak|short|characters/i.test(msg)) {
    return msg;
  }

  return msg;
}

export function friendlyOAuthCallbackError(raw: string): string {
  const msg = decodeURIComponent(raw.replace(/\+/g, " "));

  if (
    /already registered|already linked|identity already exists|user already exists|email.*already/i.test(
      msg,
    )
  ) {
    return "This email already has a Lembra account. Use Sign in (not Sign up) and choose Sign in with Google, or sign in with your password.";
  }

  if (/access_denied|user denied|cancelled/i.test(msg)) {
    return "Google sign-in was cancelled. You can try again when you're ready.";
  }

  return msg;
}

/**
 * Supabase OAuth redirects often return errors in the URL hash or query.
 * Read once, then strip so refresh doesn't re-toast.
 */
export function consumeAuthCallbackError(): string | null {
  if (typeof window === "undefined") return null;

  const fromParams = (params: URLSearchParams): string | null => {
    const description = params.get("error_description");
    const code = params.get("error");
    if (description) return description;
    if (code && code !== "access_denied") return code;
    return null;
  };

  let err: string | null = null;
  if (window.location.hash.length > 1) {
    err = fromParams(new URLSearchParams(window.location.hash.slice(1)));
  }
  if (!err) {
    err = fromParams(new URLSearchParams(window.location.search));
  }
  if (!err) return null;

  const path = window.location.pathname;
  const search = new URLSearchParams(window.location.search);
  search.delete("error");
  search.delete("error_description");
  search.delete("error_code");
  const q = search.toString();
  window.history.replaceState(null, "", q ? `${path}?${q}` : path);

  return err;
}
